// Vendor Libs
import { applyMiddleware, createStore, compose } from 'redux';
import { createLogger } from 'redux-logger';
import promise from 'redux-promise-middleware';
import thunk from 'redux-thunk';
import { reachify } from 'redux-first-history';

// Redux Persist imports
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

// Lib Core
import freezer from './lib/core/redux-freezer';
import { reduxDev, styleLoader } from './lib/core/redux-dev';

// Reducers
import rootReducer, { reduxHistoryContext } from './reducers';

// Lib App Middlewares
import deleteRecordMiddleware from './middlewares/delete-record-middleware';
import displayPathMiddleware from './middlewares/display-path-middleware';
import fetchMiddleware from './middlewares/fetch-middleware';
import flaggedMetadataMiddleware from './middlewares/flagged-metadata-middleware';
import formMiddleware from './middlewares/form-middleware';
import graceNoteMiddleware from './middlewares/grace-note-middleware';
import mappingMiddleware from './middlewares/mapping-middleware';
import massActionDataMiddleware from './middlewares/mass-action-data-middleware';
import preferencesMiddleware from './middlewares/preferences-middleware';
import recordLockingMiddleware from './middlewares/record-locking-middleware';
import requestPasswordMiddleware from './middlewares/request-password-middleware';
import routerMiddleware from './middlewares/router-middleware';
import servicesActionMiddleware from './middlewares/services-action-caller-middleware';

const { createReduxHistory, routerMiddleware: historyMiddleware } = reduxHistoryContext;

// Configure Redux Persist
const persistConfig = {
  key: 'root',
  keyPrefix: 'metacloud:',
  storage,
  blacklist: ['nonSerializable', 'layout'],
};

// Wrap rootReducer with persistReducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create store with middleware and persisted reducer
const store = compose(
  applyMiddleware(
    promise(),
    thunk,
    freezer,
    displayPathMiddleware,
    recordLockingMiddleware,
    fetchMiddleware,
    formMiddleware,
    graceNoteMiddleware,
    preferencesMiddleware,
    mappingMiddleware,
    deleteRecordMiddleware,
    requestPasswordMiddleware,
    massActionDataMiddleware,
    flaggedMetadataMiddleware,
    historyMiddleware,
    routerMiddleware,
    servicesActionMiddleware,
    createLogger({ collapsed: true })
  ),
  reduxDev()
)(createStore)(persistedReducer);

export const history = reachify(createReduxHistory(store));

// Enable Webpack hot module replacement for reducers if available
if (module.hot) {
  module.hot.accept('./reducers', () => {
    const newRootReducer = require('./reducers').default;
    // Wrap the new reducer with persistReducer using the same persistConfig
    store.replaceReducer(persistReducer(persistConfig, newRootReducer));
  });
  styleLoader();
}

// Export persistor to be used in your application (e.g., in App.js)
export const persistor = persistStore(store);

export default store;
